import type { ReactNode } from 'react'

import { Skeleton } from '@/shared/ui/Card'

import { isAddonModule, type ModuleKey } from './module-keys'
import { ModuleLocked } from './ModuleLocked'
import { getModule } from './registry'
import { useEntitlements, useIsModuleActive } from './useModules'

export interface ModuleGuardProps {
  module: ModuleKey
  children: ReactNode
}

/**
 * Protegge una rotta di modulo.
 *
 * Non reindirizza: chi arriva su un modulo spento resta sull'indirizzo che ha
 * chiesto e vede cosa ci sarebbe. Un rimbalzo su un'altra pagina fa perdere
 * l'orientamento e non spiega niente.
 *
 * È comodità, non sicurezza: le rotte del backend rifiutano comunque le
 * richieste per un modulo non attivo.
 */
export function ModuleGuard({ module, children }: ModuleGuardProps) {
  const { isLoading } = useEntitlements()
  const isActive = useIsModuleActive(module)

  // I moduli di piattaforma non dipendono dal contratto: mostrarli dopo un
  // caricamento farebbe lampeggiare uno scheletro per una verifica inutile.
  if (!isAddonModule(module)) {
    return <>{children}</>
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <Skeleton
          className="h-8 w-64"
          label={`Verifica dell'accesso a ${getModule(module).name}`}
        />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!isActive) {
    return <ModuleLocked module={getModule(module)} />
  }

  return <>{children}</>
}
